// frontend/src/components/games/sudoku/sudokuGenerator.ts
//
// 9x9 Sudoku üretici. Memory/Snake ile hiçbir dosya paylaşılmaz.

export type SudokuGrid = number[][]; // 0 = boş hücre

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function isValidPlacement(grid: SudokuGrid, row: number, col: number, value: number): boolean {
  for (let i = 0; i < 9; i++) {
    if (grid[row][i] === value) return false;
    if (grid[i][col] === value) return false;
  }
  const boxRow = Math.floor(row / 3) * 3;
  const boxCol = Math.floor(col / 3) * 3;
  for (let r = boxRow; r < boxRow + 3; r++) {
    for (let c = boxCol; c < boxCol + 3; c++) {
      if (grid[r][c] === value) return false;
    }
  }
  return true;
}

function createEmptyGrid(): SudokuGrid {
  return Array.from({ length: 9 }, () => Array(9).fill(0));
}

function fillGrid(grid: SudokuGrid, row: number, col: number): boolean {
  if (row === 9) return true;
  const [nextRow, nextCol] = col === 8 ? [row + 1, 0] : [row, col + 1];
  const digits = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]);
  for (const digit of digits) {
    if (isValidPlacement(grid, row, col, digit)) {
      grid[row][col] = digit;
      if (fillGrid(grid, nextRow, nextCol)) return true;
      grid[row][col] = 0;
    }
  }
  return false;
}

export function generateFullSolution(): SudokuGrid {
  const grid = createEmptyGrid();
  fillGrid(grid, 0, 0);
  return grid;
}

interface SolveBudget {
  nodes: number;
  limit: number;
}

/** Verilen bulmacanın çözüm sayısını (cap'e kadar) sayar. Performans için node bütçesi sınırlıdır. */
function countSolutions(grid: SudokuGrid, cap: number, budget: SolveBudget): number {
  let row = -1;
  let col = -1;
  outer: for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      if (grid[r][c] === 0) {
        row = r;
        col = c;
        break outer;
      }
    }
  }
  if (row === -1) return 1; // tüm hücreler dolu — bir çözüm bulundu

  let count = 0;
  for (let digit = 1; digit <= 9; digit++) {
    budget.nodes += 1;
    if (budget.nodes > budget.limit) return count; // bütçe aşıldı — güvenli tarafta kal
    if (isValidPlacement(grid, row, col, digit)) {
      grid[row][col] = digit;
      count += countSolutions(grid, cap - count, budget);
      grid[row][col] = 0;
      if (count >= cap) return count;
    }
  }
  return count;
}

function cloneGrid(grid: SudokuGrid): SudokuGrid {
  return grid.map((row) => [...row]);
}

export interface SudokuPuzzle {
  puzzle: SudokuGrid;
  solution: SudokuGrid;
}

/**
 * `clueCount` kadar dolu hücre bırakarak bir bulmaca üretir.
 * Her kaldırma denemesinde, bulmacanın hâlâ tek çözümü olduğu (sınırlı bir arama
 * bütçesiyle) doğrulanır; aksi halde kaldırma geri alınır.
 */
export function generateSudokuPuzzle(clueCount: number): SudokuPuzzle {
  const solution = generateFullSolution();
  const puzzle = cloneGrid(solution);

  const coords: [number, number][] = [];
  for (let r = 0; r < 9; r++) for (let c = 0; c < 9; c++) coords.push([r, c]);
  const shuffledCoords = shuffle(coords);

  let removed = 0;
  const targetRemoved = 81 - clueCount;

  for (const [r, c] of shuffledCoords) {
    if (removed >= targetRemoved) break;
    if (puzzle[r][c] === 0) continue;

    const backup = puzzle[r][c];
    puzzle[r][c] = 0;

    const testGrid = cloneGrid(puzzle);
    const solutions = countSolutions(testGrid, 2, { nodes: 0, limit: 60_000 });

    if (solutions === 1) {
      removed++;
    } else {
      puzzle[r][c] = backup; // tek çözüm garanti edilemedi — geri al
    }
  }

  return { puzzle, solution };
}
